import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";
import type { AccountLinkKind, AccountStatus } from "@yjsgames/account-link";
import { CONNECT_LABEL_KEY, resolveAccountLinkView } from "./accountLinkView.js";
import { ACCOUNT_WARNING_ORANGE, createAccountWarningBadge } from "./accountWarningBadge.js";

/** 연동 섹션 — 네이티브(provider 있음)일 때만. 웹은 null이라 섹션 자체가 생략된다. */
export interface AccountLinkSection {
  readonly kind: AccountLinkKind;
  readonly status: AccountStatus;
  /** DIP에서 판단한 지원 여부. false면 모달이 버튼/row를 렌더하지 않는다. */
  readonly isSupported?: () => boolean;
  /** 연동 버튼 탭 — settingsBoot이 provider.link 결선(성공 시 updateAccountLinkStatus로 갱신). */
  readonly onConnect: () => void;
}

export interface AccountLinkModalOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  /** 연동 섹션 — 네이티브면 {kind,status,onConnect}, 웹/미지원이면 null(삭제 버튼만). */
  readonly link: AccountLinkSection | null;
  /** 추가 연동 섹션들 — 향후 복수 플랫폼/계정 연동 버튼을 같은 영역에 쌓기 위한 확장 슬롯. */
  readonly links?: readonly AccountLinkSection[];
  /** 계정 삭제 탭 — settingsBoot이 yes/no 확인 팝업을 띄운 뒤 resetSave. */
  readonly onDeleteAccount: () => void;
  readonly onClose: () => void;
}

const PANEL_W = 280;
const PANEL_H_LINK_BASE = 322;
const PANEL_H_NOLINK = 150;
const LINK_ROW_START_Y = 146;
const LINK_ROW_STEP = 58;
const panelHeight = (linkCount: number): number =>
  linkCount > 0 ? PANEL_H_LINK_BASE + Math.max(0, linkCount - 1) * LINK_ROW_STEP : PANEL_H_NOLINK;

/** 모달 내 "계정 삭제" 버튼 중심 y — E2E가 클릭 좌표 계산(레이아웃 변경 자동 추종). hasLink로 패널 높이 분기. */
export const accountModalDeleteBtnCenterY = (screenHeight: number, hasLink: boolean): number =>
  screenHeight / 2 + panelHeight(hasLink ? 1 : 0) / 2 - 30;

interface AccountLinkRowView {
  readonly kind: AccountLinkKind;
  readonly statusText: Phaser.GameObjects.Text;
  readonly connectBtn: NineSliceButton;
  readonly alreadyText: Phaser.GameObjects.Text;
  connecting: boolean;
}

/**
 * "계정 연동 및 삭제" 모달 — GridUnlockConfirmPopup 패턴(ModalLayer + NineSlicePanel). 설정 모달의
 * "계정 연동 및 삭제" 버튼이 띄운다(설정 위에 스택).
 *  - 네이티브(link != null): 플랫폼(Android=Play Games / iOS=Game Center) 연동 상태 + 연동 버튼(미연동)
 *    / "이미 연동되었습니다"(연동됨). 연동 성공 시 setStatus로 인플레이스 갱신.
 *  - 웹(link == null): 연동 섹션 생략, "계정 삭제" 버튼만.
 *  - 공통: 하단 "계정 삭제" 버튼 → onDeleteAccount(확인 팝업 경유 resetSave).
 */
export class AccountLinkModal extends ModalLayer {
  private readonly rows: AccountLinkRowView[] = [];
  private readonly guestWarningBadge?: Phaser.GameObjects.Container;
  private readonly guestWarningText?: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, opts: AccountLinkModalOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onClose(),
    });

    const rawLinks = opts.links ?? (opts.link ? [opts.link] : []);
    const links = rawLinks.filter((link) => link.isSupported?.() ?? true);
    const ph = panelHeight(links.length);
    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const top = cy - ph / 2;
    this.setBackdropCloseExclusion(cx - PANEL_W / 2, top, PANEL_W, ph);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: PANEL_W,
      height: ph,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    const title = scene.add
      .text(cx, top + 22, t("settings.accountManage"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textBright,
      })
      .setOrigin(0.5);
    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, PANEL_W, 18, getTextDirection()), top + 16, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onClose());
    this.contentContainer.add([title, closeBtn]);

    // 연동 섹션(네이티브) — 아이콘 + 상태 라벨 + 연동버튼/이미연동 안내. 웹은 생략.
    if (links.length > 0) {
      this.guestWarningBadge = createAccountWarningBadge(scene, cx - 104, top + 62, { size: 15 });
      this.guestWarningText = scene.add
        .text(cx + 10, top + 62, t("settings.linkAccount.guestWarning"), {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: `#${ACCOUNT_WARNING_ORANGE.toString(16)}`,
          align: "left",
          wordWrap: { width: 210 },
        })
        .setOrigin(0.5);

      this.contentContainer.add([this.guestWarningBadge, this.guestWarningText]);

      links.forEach((link, index) => {
        const rowY = top + LINK_ROW_START_Y + index * LINK_ROW_STEP;
        const statusText = scene.add
          .text(cx, rowY - 24, "", {
            fontFamily: FONT_FAMILY,
            fontSize: TYPO.caption,
            color: PALETTE.textDim,
          })
          .setOrigin(0.5);
        const connectBtn = new NineSliceButton(scene, cx, rowY + 8, {
          style: PRIMARY_BUTTON_STYLE,
          label: t(CONNECT_LABEL_KEY[link.kind]),
          iconKey: "icon_account_link",
          widthOverride: 220,
          heightOverride: 38,
        });
        const alreadyText = scene.add
          .text(cx, rowY + 8, t("settings.linkAccount.already"), {
            fontFamily: FONT_FAMILY,
            fontSize: TYPO.body,
            color: PALETTE.textDim,
          })
          .setOrigin(0.5);
        const row: AccountLinkRowView = {
          kind: link.kind,
          statusText,
          connectBtn,
          alreadyText,
          connecting: false,
        };
        connectBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
          this.setRowConnecting(row, true);
          link.onConnect();
        });
        this.rows.push(row);
        this.contentContainer.add([statusText, connectBtn, alreadyText]);
        this.applyRowStatus(row, link.status);
      });
      this.syncGuestWarningVisibility();
    }

    // 계정 삭제 버튼(항상) — 하단. 클릭 → onDeleteAccount(확인 팝업 경유). 위험 강조는 확인 팝업이 담당.
    const deleteBtn = new NineSliceButton(scene, cx, cy + ph / 2 - 30, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("settings.deleteAccount"),
      iconKey: "icon_account_delete",
      widthOverride: 220,
      heightOverride: 38,
    });
    deleteBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onDeleteAccount());
    this.contentContainer.add(deleteBtn);
  }

  /** 연동 성공 후 상태 라벨 + 연동버튼↔안내 토글 인플레이스 갱신(연동 섹션 없으면 no-op). */
  setStatus(status: AccountStatus): void {
    const row = this.rows[0];
    if (!row) return;
    row.connecting = false;
    this.applyRowStatus(row, status);
    this.syncGuestWarningVisibility();
  }

  setConnecting(value: boolean): void {
    const row = this.rows[0];
    if (!row) return;
    this.setRowConnecting(row, value);
  }

  private setRowConnecting(row: AccountLinkRowView, value: boolean): void {
    row.connecting = value;
    row.connectBtn.setEnabled(!value);
    row.connectBtn.setBgAlpha(value ? 0.65 : 1);
    row.connectBtn.setLabel(value ? t("settings.linkAccount.connecting") : t(CONNECT_LABEL_KEY[row.kind]));
  }

  private applyRowStatus(row: AccountLinkRowView, status: AccountStatus): void {
    const view = resolveAccountLinkView(status, row.kind);
    row.statusText.setText(t(view.statusKey, { name: view.statusName }));
    row.statusText.setColor(view.showConnect ? PALETTE.textDim : PALETTE.textPrimary);
    row.connectBtn.setVisible(view.showConnect);
    row.alreadyText.setVisible(!view.showConnect);
    if (!row.connecting) row.connectBtn.setLabel(t(view.connectLabelKey));
    row.connectBtn.setEnabled(!row.connecting);
    row.connectBtn.setBgAlpha(row.connecting ? 0.65 : 1);
  }

  private syncGuestWarningVisibility(): void {
    const needsWarning = this.rows.some((row) => row.connectBtn.visible);
    this.guestWarningBadge?.setVisible(needsWarning);
    this.guestWarningText?.setVisible(needsWarning);
  }
}
